import React, { useEffect, useState } from 'react';
import { TentIcon, QrCodeIcon, PlusIcon, SearchIcon, FilterIcon, CalendarIcon, MapPinIcon, UsersIcon, ClockIcon, ArrowUpDownIcon, EyeIcon, EditIcon, TrashIcon, CheckIcon, XIcon, ArrowRightIcon, ExternalLinkIcon, Share2Icon, PresentationIcon, DollarSignIcon, PercentIcon } from 'lucide-react';
import { FairForm } from './FairForm';
// Mock data for agricultural fairs
const mockFairs = [{
  id: 1,
  name: 'Agri Expo 2025',
  location: 'Madrid, Spain',
  startDate: '2025-11-15',
  endDate: '2025-11-20',
  status: 'upcoming',
  attendees: 5000,
  description: 'The largest agricultural fair in Europe showcasing the latest irrigation technologies.',
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 2,
  name: 'Farm Tech Show',
  location: 'Chicago, USA',
  startDate: '2025-12-05',
  endDate: '2025-12-10',
  status: 'upcoming',
  attendees: 8000,
  description: 'An international exhibition of agricultural machinery and equipment.',
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 3,
  name: 'Irrigation Conference 2025',
  location: 'Dubai, UAE',
  startDate: '2025-10-12',
  endDate: '2025-10-15',
  status: 'upcoming',
  attendees: 3000,
  description: 'A specialized conference focused on innovative irrigation solutions for arid regions.',
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 4,
  name: 'AgroTech Summit',
  location: 'Berlin, Germany',
  startDate: '2025-02-18',
  endDate: '2025-02-22',
  status: 'upcoming',
  attendees: 6000,
  description: 'A summit bringing together agricultural technology innovators and farmers.',
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 5,
  name: 'Paris Agricultural Show (SIA)',
  location: 'Paris, France',
  startDate: '2025-02-24',
  endDate: '2025-03-03',
  status: 'upcoming',
  attendees: 12000,
  description: 'One of the most important agricultural events in France, showcasing livestock, crops, and agricultural machinery.',
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 6,
  name: 'Agritechnica',
  location: 'Hannover, Germany',
  startDate: '2025-11-12',
  endDate: '2025-11-18',
  status: 'upcoming',
  attendees: 15000,
  description: "The world's leading trade fair for agricultural machinery and equipment.",
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 7,
  name: 'World Ag Expo',
  location: 'Tulare, USA',
  startDate: '2025-02-13',
  endDate: '2025-02-15',
  status: 'upcoming',
  attendees: 10000,
  description: 'The largest annual agricultural exposition in the world with over 1,400 exhibitors.',
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 8,
  name: 'International Green Week',
  location: 'Berlin, Germany',
  startDate: '2025-01-19',
  endDate: '2025-01-28',
  status: 'upcoming',
  attendees: 8500,
  description: 'A unique exhibition for the food, agriculture, and horticulture industries.',
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 9,
  name: 'EIMA International',
  location: 'Bologna, Italy',
  startDate: '2025-11-06',
  endDate: '2025-11-10',
  status: 'upcoming',
  attendees: 9000,
  description: 'An international exhibition of machinery for agriculture and gardening.',
  hasQRCode: false,
  promotionDiscount: 40
}];
export function FairsManagement({
  onGenerateQR
}) {
  const [fairs, setFairs] = useState(mockFairs);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('startDate');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isAddingFair, setIsAddingFair] = useState(false);
  const [isEditingFair, setIsEditingFair] = useState(false);
  const [currentFair, setCurrentFair] = useState(null);
  // Filter and sort fairs
  const filteredFairs = fairs.filter(fair => {
    const matchesSearch = fair.name.toLowerCase().includes(searchQuery.toLowerCase()) || fair.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || fair.status === filterStatus;
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'name') {
      comparison = a.name.localeCompare(b.name);
    } else if (sortBy === 'location') {
      comparison = a.location.localeCompare(b.location);
    } else if (sortBy === 'startDate') {
      comparison = new Date(a.startDate) - new Date(b.startDate);
    } else if (sortBy === 'attendees') {
      comparison = a.attendees - b.attendees;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  const handleSort = column => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };
  const handleAddFair = fair => {
    setFairs([...fairs, {
      ...fair,
      id: fairs.length + 1,
      hasQRCode: false,
      promotionDiscount: 40
    }]);
    setIsAddingFair(false);
  };
  const handleEditFair = fair => {
    setFairs(fairs.map(f => f.id === fair.id ? fair : f));
    setIsEditingFair(false);
    setCurrentFair(null);
  };
  const handleDeleteFair = id => {
    if (window.confirm('Are you sure you want to delete this fair?')) {
      setFairs(fairs.filter(fair => fair.id !== id));
    }
  };
  const handleGenerateQR = fair => {
    if (onGenerateQR) {
      onGenerateQR(fair);
    }
  };
  // Format date range for display
  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    // Format options
    const options = {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    };
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  };
  // Calculate fair status
  const getFairStatus = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (now < start) return 'upcoming';
    if (now >= start && now <= end) return 'active';
    return 'past';
  };
  // Get status badge color
  const getStatusColor = status => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'past':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default:
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
  };
  if (isAddingFair) {
    return <FairForm onSubmit={handleAddFair} onCancel={() => setIsAddingFair(false)} data-id="element-146" />;
  }
  if (isEditingFair && currentFair) {
    return <FairForm fair={currentFair} onSubmit={handleEditFair} onCancel={() => {
      setIsEditingFair(false);
      setCurrentFair(null);
    }} data-id="element-147" />;
  }
  return <div data-id="element-148">
      <div className="flex justify-between items-center mb-6" data-id="element-149">
        <div data-id="element-150">
          <h2 className="text-2xl font-bold text-white flex items-center" data-id="element-151">
            <TentIcon className="mr-2" size={24} data-id="element-152" />
            Agricultural Fairs Management
          </h2>
          <p className="text-blue-300 mt-1" data-id="element-153">
            Register and manage agricultural fairs, generate promotional QR
            codes
          </p>
        </div>
        <button onClick={() => setIsAddingFair(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors shadow-md flex items-center" data-id="element-154">
          <PlusIcon size={18} className="mr-2" data-id="element-155" />
          Register Fair
        </button>
      </div>
      {/* Filters and search */}
      <div className="bg-white/5 backdrop-blur-sm border border-blue-800/30 rounded-xl p-4 mb-6" data-id="element-156">
        <div className="flex flex-wrap items-center gap-4" data-id="element-157">
          <div className="flex-1 min-w-[240px]" data-id="element-158">
            <div className="relative" data-id="element-159">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-300" size={18} data-id="element-160" />
              <input type="text" placeholder="Search fairs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white/10 border border-blue-800/30 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-161" />
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-162">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-white/10 border border-blue-800/30 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none" data-id="element-163">
              <option value="all" data-id="element-164">All Statuses</option>
              <option value="upcoming" data-id="element-165">Upcoming</option>
              <option value="active" data-id="element-166">Active</option>
              <option value="past" data-id="element-167">Past</option>
            </select>
            <button className="flex items-center gap-2 bg-white/10 text-blue-300 hover:bg-white/20 px-4 py-2.5 rounded-lg transition-colors" data-id="element-168">
              <FilterIcon size={18} data-id="element-169" />
              More Filters
            </button>
          </div>
        </div>
      </div>
      {/* Fairs Table */}
      <div className="bg-white/5 backdrop-blur-sm border border-blue-800/30 rounded-xl shadow-xl overflow-hidden" data-id="element-170">
        <div className="overflow-x-auto" data-id="element-171">
          <table className="w-full" data-id="element-172">
            <thead data-id="element-173">
              <tr className="border-b border-blue-800/30" data-id="element-174">
                <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-175">
                  <button className="flex items-center focus:outline-none" onClick={() => handleSort('name')} data-id="element-176">
                    Fair Name
                    <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'name' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-177" />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-178">
                  <button className="flex items-center focus:outline-none" onClick={() => handleSort('location')} data-id="element-179">
                    Location
                    <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'location' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-180" />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-181">
                  <button className="flex items-center focus:outline-none" onClick={() => handleSort('startDate')} data-id="element-182">
                    Date Range
                    <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'startDate' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-183" />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-184">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-185">
                  <button className="flex items-center focus:outline-none" onClick={() => handleSort('attendees')} data-id="element-186">
                    Expected Attendees
                    <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'attendees' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-187" />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-188">
                  QR Code
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-189">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-blue-800/30" data-id="element-190">
              {filteredFairs.map(fair => <tr key={fair.id} className="hover:bg-blue-800/20 transition-colors" data-id="element-191">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-192">
                    <div className="flex items-center" data-id="element-193">
                      <div className="flex-shrink-0 h-10 w-10 bg-amber-900/30 rounded-full flex items-center justify-center border border-amber-700/30" data-id="element-194">
                        <TentIcon className="h-5 w-5 text-amber-500" aria-hidden="true" data-id="element-195" />
                      </div>
                      <div className="ml-4" data-id="element-196">
                        <div className="text-sm font-medium text-white" data-id="element-197">
                          {fair.name}
                        </div>
                        <div className="text-xs text-blue-300" data-id="element-198">
                          ID: FAIR-{fair.id.toString().padStart(4, '0')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-199">
                    <div className="flex items-center text-sm text-white" data-id="element-200">
                      <MapPinIcon size={16} className="mr-1 text-blue-400" data-id="element-201" />
                      {fair.location}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-202">
                    <div className="flex items-center text-sm text-white" data-id="element-203">
                      <CalendarIcon size={16} className="mr-1 text-blue-400" data-id="element-204" />
                      {formatDateRange(fair.startDate, fair.endDate)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-205">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(fair.status)}`} data-id="element-206">
                      {fair.status === 'upcoming' && <ClockIcon size={12} className="mr-1" data-id="element-207" />}
                      {fair.status === 'active' && <CheckIcon size={12} className="mr-1" data-id="element-208" />}
                      {fair.status === 'past' && <XIcon size={12} className="mr-1" data-id="element-209" />}
                      {fair.status.charAt(0).toUpperCase() + fair.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-210">
                    <div className="flex items-center text-sm text-white" data-id="element-211">
                      <UsersIcon size={16} className="mr-1 text-blue-400" data-id="element-212" />
                      {fair.attendees.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-213">
                    {fair.hasQRCode ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30" data-id="element-214">
                        <CheckIcon size={12} className="mr-1" data-id="element-215" />
                        Generated
                      </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30" data-id="element-216">
                        <XIcon size={12} className="mr-1" data-id="element-217" />
                        Not Generated
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-218">
                    <div className="flex justify-end space-x-2" data-id="element-219">
                      {!fair.hasQRCode && <button onClick={() => handleGenerateQR(fair)} className="text-purple-400 hover:text-purple-300 bg-purple-900/30 hover:bg-purple-800/50 px-3 py-1.5 rounded-md transition-colors inline-flex items-center" data-id="element-220">
                          <QrCodeIcon size={16} className="mr-1" data-id="element-221" />
                          Generate QR
                        </button>}
                      {fair.hasQRCode && <button onClick={() => handleGenerateQR(fair)} className="text-purple-400 hover:text-purple-300 bg-purple-900/30 hover:bg-purple-800/50 px-3 py-1.5 rounded-md transition-colors inline-flex items-center" data-id="element-222">
                          <EyeIcon size={16} className="mr-1" data-id="element-223" />
                          View QR
                        </button>}
                      <button onClick={() => {
                    setCurrentFair(fair);
                    setIsEditingFair(true);
                  }} className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 p-1.5 rounded-md transition-colors" data-id="element-224">
                        <EditIcon size={16} data-id="element-225" />
                      </button>
                      <button onClick={() => handleDeleteFair(fair.id)} className="text-red-400 hover:text-red-300 bg-red-900/30 hover:bg-red-800/50 p-1.5 rounded-md transition-colors" data-id="element-226">
                        <TrashIcon size={16} data-id="element-227" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {filteredFairs.length === 0 && <div className="text-center py-12" data-id="element-228">
            <TentIcon size={48} className="mx-auto text-blue-800/50 mb-4" data-id="element-229" />
            <h3 className="text-lg font-medium text-white mb-1" data-id="element-230">
              No fairs found
            </h3>
            <p className="text-blue-300 mb-4" data-id="element-231">
              Try adjusting your search or filters
            </p>
            <button onClick={() => setIsAddingFair(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors shadow-md inline-flex items-center" data-id="element-232">
              <PlusIcon size={18} className="mr-2" data-id="element-233" />
              Register New Fair
            </button>
          </div>}
      </div>
      {/* Promotion Info */}
      <div className="mt-6 bg-gradient-to-r from-amber-600/20 to-amber-700/20 backdrop-blur-sm border border-amber-500/30 rounded-xl p-5 relative overflow-hidden" data-id="element-234">
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full filter blur-3xl -z-10" data-id="element-235"></div>
        <div className="flex items-start" data-id="element-236">
          <div className="p-3 bg-gradient-to-br from-amber-500/20 to-amber-700/20 rounded-lg inline-flex mb-4 mr-4" data-id="element-237">
            <PercentIcon size={24} className="text-amber-400" data-id="element-238" />
          </div>
          <div data-id="element-239">
            <h3 className="text-lg font-bold text-white mb-1" data-id="element-240">
              Fair Promotion Details
            </h3>
            <p className="text-blue-200" data-id="element-241">
              When generating QR codes for fairs, a special 40% discount
              promotion will be automatically applied to any quotes created
              using the QR code.
            </p>
            <div className="mt-4 flex flex-wrap gap-4" data-id="element-242">
              <div className="bg-white/10 rounded-lg p-3 flex items-center" data-id="element-243">
                <DollarSignIcon size={20} className="text-green-400 mr-2" data-id="element-244" />
                <div data-id="element-245">
                  <div className="text-sm text-white font-medium" data-id="element-246">
                    40% Discount
                  </div>
                  <div className="text-xs text-blue-300" data-id="element-247">
                    Applied to all fair quotes
                  </div>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-3 flex items-center" data-id="element-248">
                <PresentationIcon size={20} className="text-blue-400 mr-2" data-id="element-249" />
                <div data-id="element-250">
                  <div className="text-sm text-white font-medium" data-id="element-251">
                    Display Mode
                  </div>
                  <div className="text-xs text-blue-300" data-id="element-252">
                    Optimized for kiosk screens
                  </div>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-3 flex items-center" data-id="element-253">
                <Share2Icon size={20} className="text-purple-400 mr-2" data-id="element-254" />
                <div data-id="element-255">
                  <div className="text-sm text-white font-medium" data-id="element-256">
                    Sharable Links
                  </div>
                  <div className="text-xs text-blue-300" data-id="element-257">
                    Easy to distribute
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}